import { api } from './client';

/** One office's type for a holiday that varies by office. */
export interface HolidayRule {
  locationId: number;
  locationName: string;
  isMandatory: boolean;
}

export interface Holiday {
  id: number;
  date: string;
  name: string;
  /** The type at every office when `rules` is empty. */
  isMandatory: boolean;
  /** Empty = same for all offices; otherwise an office not listed has no holiday. */
  rules: HolidayRule[];
}

export interface HolidayLocation {
  id: number;
  label: string;
}

export type HolidayRuleInput = Pick<HolidayRule, 'locationId' | 'isMandatory'>;

/** A holiday's type at one office (matched by id or name); null = not a holiday there. */
export function holidayKindAt(
  h: Pick<Holiday, 'isMandatory'> & { rules?: Array<Pick<HolidayRule, 'locationId' | 'locationName' | 'isMandatory'>> },
  office: { id?: number | null; name?: string | null },
): 'mandatory' | 'optional' | null {
  const rules = h.rules ?? [];
  if (rules.length === 0) return h.isMandatory ? 'mandatory' : 'optional';
  const rule = rules.find((r) => (office.id != null && r.locationId === office.id) || (office.name != null && r.locationName === office.name));
  if (!rule) return null;
  return rule.isMandatory ? 'mandatory' : 'optional';
}

export function listHolidays(year?: number): Promise<{ holidays: Holiday[]; locations: HolidayLocation[] }> {
  const qs = year ? `?year=${year}` : '';
  return api.get<{ holidays: Holiday[]; locations: HolidayLocation[] }>(`/api/admin/holidays${qs}`);
}

export function createHoliday(data: { date: string; name: string; isMandatory: boolean; rules: HolidayRuleInput[] }): Promise<Holiday> {
  return api.post<Holiday>('/api/admin/holidays', data);
}

export function updateHoliday(id: number, fields: { name: string; isMandatory: boolean; rules: HolidayRuleInput[] }): Promise<Holiday> {
  return api.patch<Holiday>(`/api/admin/holidays/${id}`, fields);
}

export function deleteHoliday(id: string): Promise<void> {
  return api.delete<void>(`/api/admin/holidays/${id}`);
}

export interface ImportResult {
  created: number;
  skipped: number;
  errors: { row: number; reason: string }[];
}

export function importHolidays(file: File): Promise<ImportResult> {
  const form = new FormData();
  form.append('file', file);
  return api.postForm<ImportResult>('/api/admin/holidays/import', form);
}

export function exportHolidays(year?: number): string {
  const base = '/api/admin/holidays/export';
  return year ? `${base}?year=${year}` : base;
}
